import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  Chip,
  Checkbox,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Undo as UndoIcon
} from '@mui/icons-material';

// Import shared components
import FieldWithModificationIndicator from '../../shared/FieldWithModificationIndicator';
import LocalTextField from '../../shared/LocalTextField';
import LocalSelect from '../../shared/LocalSelect';
import LocalNotesField from '../../shared/LocalNotesField';
import TextbelegeEditor from '../../shared/TextbelegeEditor';

// Types
interface QSAnforderung {
  nummer: number;
  name: string;
  beschreibung: string;
  abdeckung: number;
  selbstbewertung: string;
  erweiterung?: string;
  Sicherheitsskala?: string;
  textbelege?: any[];
}

interface QSRequirementCardProps {
  anforderung: QSAnforderung;
  originalAnforderung: QSAnforderung; // Ursprüngliche KI-generierte Daten
  isModified: boolean;
  canEdit: boolean;
  isQS: boolean;
  isSuperQS: boolean;
  isAdmin: boolean;
  isReviewed: boolean;
  saving: boolean;
  // Value getters
  getQSAnforderungValue: (nummer: number, field: string, defaultValue: any) => any;
  // Modification checkers
  isFieldModified: (nummer: number, field: string) => boolean;
  isTextbelegFieldModified: (nummer: number, belegIndex: number, field: string) => boolean;
  isNotesModified: (nummer: number) => boolean;
  // Notes
  anforderungNotes: {[key: string]: string};
  // Handlers
  onLocalFieldChange: (nummer: number, field: string, value: string) => void;
  onTextbelegeChange: (nummer: number, textbelege: any[]) => void;
  onAnforderungNotesChange: (nummer: number, value: string) => void;
  onReviewedToggle: (nummer: number) => void;
  onResetAnforderung: (nummer: number) => void;
}

const QSRequirementCard: React.FC<QSRequirementCardProps> = ({
  anforderung,
  originalAnforderung,
  isModified,
  canEdit,
  isQS,
  isSuperQS,
  isAdmin,
  isReviewed,
  saving,
  getQSAnforderungValue,
  isFieldModified,
  isTextbelegFieldModified,
  isNotesModified,
  anforderungNotes,
  onLocalFieldChange,
  onTextbelegeChange,
  onAnforderungNotesChange,
  onReviewedToggle,
  onResetAnforderung
}) => {
  const canSuperQSReview = (isSuperQS || isAdmin);

  return (
    <Accordion 
      sx={{ 
        mb: 1,
        // Visuelle Markierung für überarbeitete Anforderungen
        border: isModified ? '2px solid #ff9800' : '1px solid #e0e0e0',
        backgroundColor: isModified ? '#fff3e0' : 'white',
        '&:hover': {
          backgroundColor: isModified ? '#ffecb3' : '#f5f5f5'
        }
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" gap={1} width="100%">
          {/* QS-Prüfungsmarkierung für QS-Benutzer */}
          {(isQS || isSuperQS || isAdmin) && canEdit && (
            <Tooltip title={isReviewed ? "Als geprüft markiert - Klicken zum Aufheben" : "Als geprüft markieren"}>
              <Checkbox
                checked={isReviewed}
                onChange={() => onReviewedToggle(anforderung.nummer)}
                color="primary"
                size="small"
                onClick={(e) => e.stopPropagation()} // Verhindert Accordion-Toggle
              />
            </Tooltip>
          )}
          
          {/* SuperQS Sichtbarkeit der Prüfung (read-only) */}
          {(isSuperQS || isAdmin) && !canEdit && isReviewed && (
              <Checkbox
                checked={true}
                color="primary"
                size="small"
                disabled
                onClick={(e) => e.stopPropagation()} // Verhindert Accordion-Toggle
              />
          )}
          
          <Typography 
            variant="subtitle1"
            sx={{ 
              fontWeight: isModified ? 'bold' : 'normal',
              color: isModified ? '#e65100' : 'inherit',
              flex: 1
            }}
          >
            {anforderung.nummer}. {anforderung.name}
          </Typography>
          <Chip 
            label={Number(getQSAnforderungValue(anforderung.nummer, 'abdeckung', anforderung.abdeckung)) === 1 ? 'Erfüllt' : 'Nicht erfüllt'} 
            color={Number(getQSAnforderungValue(anforderung.nummer, 'abdeckung', anforderung.abdeckung)) === 1 ? 'success' : 'error'}
            size="small"
          />
          {isModified && (
            <Chip 
              label="Überarbeitet" 
              color="warning" 
              size="small"
              icon={<EditIcon />}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Linke Seite: Original (nur lesbar) */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Original (KI-generiert)
            </Typography>
            <Box sx={{ p: 1, backgroundColor: '#f8f9fa', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Beschreibung:</strong>
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                {originalAnforderung.beschreibung}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Abdeckung:</strong>
              </Typography>
              <Chip 
                label={originalAnforderung.abdeckung === 1 ? 'Erfüllt' : 'Nicht erfüllt'} 
                color={originalAnforderung.abdeckung === 1 ? 'success' : 'error'}
                size="small"
                sx={{ mb: 1, fontSize: '0.875rem' }}
              />
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Selbstbewertung:</strong>
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                {originalAnforderung.selbstbewertung}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Erweiterung:</strong>
              </Typography>
              <Typography variant="body2" paragraph sx={{ fontSize: '0.875rem' }}>
                {originalAnforderung.erweiterung || 'N/A'}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Sicherheitsskala:</strong>
              </Typography>
              <Chip 
                label={originalAnforderung.Sicherheitsskala || 'N/A'} 
                size="small"
                sx={{ mb: 1, fontSize: '0.875rem' }}
              />
              
              <Typography variant="body2" color="textSecondary" gutterBottom>
                <strong>Textbelege ({originalAnforderung.textbelege?.length || 0}):</strong>
              </Typography>
              {originalAnforderung.textbelege?.map((beleg: any, belegIndex: number) => (
                <Box key={belegIndex} sx={{ mb: 1, p: 1, backgroundColor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Quelle:</strong> {beleg.quellenangabe}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                    {beleg.textauszug}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    <strong>Relevanz:</strong> {beleg.relevanz}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          
          {/* Rechte Seite: Editierbare Kopie */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="secondary" gutterBottom>
              QS-Bearbeitung {isModified && <Chip label="Geändert" color="warning" size="small" />}
            </Typography>
            <Box sx={{ p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: isModified ? '2px solid #ff9800' : '1px solid #e0e0e0' }}>
              <FieldWithModificationIndicator 
                isModified={isFieldModified(anforderung.nummer, 'beschreibung')}
                label="Beschreibung"
              >
                <LocalTextField
                  label="Beschreibung"
                  initialValue={getQSAnforderungValue(anforderung.nummer, 'beschreibung', anforderung.beschreibung)}
                  onLocalChange={onLocalFieldChange}
                  anforderungNummer={anforderung.nummer}
                  field="beschreibung"
                  disabled={!canEdit}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
              </FieldWithModificationIndicator>
              
              <FieldWithModificationIndicator 
                isModified={isFieldModified(anforderung.nummer, 'abdeckung')}
                label="Abdeckung"
              >
                <LocalSelect
                  label="Abdeckung"
                  initialValue={String(getQSAnforderungValue(anforderung.nummer, 'abdeckung', anforderung.abdeckung))}
                  onLocalChange={onLocalFieldChange}
                  anforderungNummer={anforderung.nummer}
                  field="abdeckung"
                  disabled={!canEdit}
                  options={[
                    { value: '0', label: 'Nicht erfüllt' },
                    { value: '1', label: 'Erfüllt' }
                  ]}
                  sx={{ mb: 2 }}
                />
              </FieldWithModificationIndicator>
              
              <FieldWithModificationIndicator 
                isModified={isFieldModified(anforderung.nummer, 'selbstbewertung')}
                label="Selbstbewertung"
              >
                <LocalTextField
                  label="Selbstbewertung"
                  initialValue={getQSAnforderungValue(anforderung.nummer, 'selbstbewertung', anforderung.selbstbewertung)}
                  onLocalChange={onLocalFieldChange}
                  anforderungNummer={anforderung.nummer}
                  field="selbstbewertung"
                  disabled={!canEdit}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              </FieldWithModificationIndicator>
              
              <FieldWithModificationIndicator 
                isModified={isFieldModified(anforderung.nummer, 'erweiterung')}
                label="Erweiterung"
              >
                <LocalTextField
                  label="Erweiterung"
                  initialValue={getQSAnforderungValue(anforderung.nummer, 'erweiterung', anforderung.erweiterung || '')}
                  onLocalChange={onLocalFieldChange}
                  anforderungNummer={anforderung.nummer}
                  field="erweiterung"
                  disabled={!canEdit}
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              </FieldWithModificationIndicator>
              
              <FieldWithModificationIndicator 
                isModified={isFieldModified(anforderung.nummer, 'Sicherheitsskala')}
                label="Sicherheitsskala"
              >
                <LocalSelect
                  label="Sicherheitsskala"
                  initialValue={getQSAnforderungValue(anforderung.nummer, 'Sicherheitsskala', anforderung.Sicherheitsskala || '')}
                  onLocalChange={onLocalFieldChange}
                  anforderungNummer={anforderung.nummer}
                  field="Sicherheitsskala"
                  disabled={!canEdit}
                  options={[
                    { value: 'Hoch', label: 'Hoch' },
                    { value: 'Mittel', label: 'Mittel' },
                    { value: 'Gering', label: 'Gering' }
                  ]}
                  sx={{ mb: 2 }}
                />
              </FieldWithModificationIndicator>
              
              <TextbelegeEditor
                anforderungNummer={anforderung.nummer}
                originalTextbelege={anforderung.textbelege || []}
                canEdit={canEdit}
                onTextbelegeChange={onTextbelegeChange}
                isFieldModified={isTextbelegFieldModified}
              />

              {/* Anforderung-spezifische Notizen */}
              <FieldWithModificationIndicator 
                isModified={isNotesModified(anforderung.nummer)}
                label="Notizen"
              >
                <LocalNotesField
                  label={`Notizen zu Anforderung ${anforderung.nummer}`}
                  initialValue={anforderungNotes[anforderung.nummer] || ''}
                  onLocalChange={(value) => onAnforderungNotesChange(anforderung.nummer, value)}
                  disabled={!canEdit}
                  placeholder={`QS-Notizen für "${anforderung.name}"...`}
                />
              </FieldWithModificationIndicator>
              
              {isModified && canEdit && (
                <Box display="flex" gap={1} mt={1}>
                  <Tooltip title="Änderungen zurücksetzen">
                    <IconButton
                      size="small"
                      onClick={() => onResetAnforderung(anforderung.nummer)}
                      disabled={saving}
                    >
                      <UndoIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default QSRequirementCard;